import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Seo from "@/components/common/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { formatDealPrice } from "@/lib/dealPrice";
import { timeAgo } from "@/lib/format";
import { toast } from "sonner";
import {
  Tag, Loader2, Plus, Clock, CheckCircle2, XCircle, PauseCircle, PlayCircle,
  Ticket, Store, Pencil, Trash2, BarChart3, Search,
} from "lucide-react";

/**
 * My deals — vendor deal dashboard (Iter 751 + 754 + 757).
 * `GET /api/classifieds/deals/mine` returns deals the user submitted OR
 * owns; rows carry is_owner, vendor_paused, is_active, vouchers_claimed,
 * vouchers_redeemed and may be status pending_owner / owner_declined.
 */
export default function MyDealsPage() {
  const { user } = useAuth();
  const q = useQuery({
    queryKey: ["my-deals", user?.id],
    queryFn: async () => {
      const { data } = await sharedApi.get("/classifieds/deals/mine");
      return data.deals || [];
    },
    enabled: !!user,
    staleTime: 30_000,
  });

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-8 md:py-12" data-testid="my-deals-page">
      <Seo title="My deals" />
      <Breadcrumbs items={[{ label: "My deals" }]} />

      <div className="mt-6 flex items-start md:items-center justify-between gap-4 flex-col md:flex-row">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-black text-slate-900">My deals</h1>
          <p className="mt-2 text-slate-500">Track submissions, pause live deals, redeem vouchers, and post new deals.</p>
        </div>
        <Link
          to="/deals/post"
          data-testid="my-deals-post-cta"
          className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white font-bold px-5 py-2.5 shadow-card"
        >
          <Plus className="w-4 h-4" /> Post your deal
        </Link>
      </div>

      {q.isLoading && (
        <div className="mt-16 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      )}
      {!q.isLoading && q.isError && (
        <div className="mt-12 rounded-2xl border border-red-200 bg-red-50 p-6 text-center text-red-700">
          Couldn&apos;t load your deals.{" "}
          <button onClick={() => q.refetch()} className="ml-1 font-bold underline">Retry</button>
        </div>
      )}
      {!q.isLoading && !q.isError && (q.data || []).length === 0 && (
        <div className="mt-12 rounded-2xl border border-dashed border-soft bg-white p-12 text-center">
          <Tag className="w-10 h-10 mx-auto text-slate-300" />
          <div className="mt-3 font-display font-bold text-slate-900 text-lg">No deals yet</div>
          <p className="mt-1 text-sm text-slate-500">Submit your first deal — reviews usually happen within 24 hours.</p>
          <Link to="/deals/post" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white font-bold px-5 py-2.5">
            <Plus className="w-4 h-4" /> Post your deal
          </Link>
        </div>
      )}

      <div className="mt-8 space-y-4">
        {(q.data || []).map((d) => (
          <MyDealRow key={d.id} deal={d} />
        ))}
      </div>
    </div>
  );
}

/** Iter 751 — chips: Live · Paused · Pending review · Awaiting your
 *  approval · You declined · Rejected. */
function StatusChip({ deal }) {
  const norm = (deal.status || "").toLowerCase();
  let it;
  if (norm === "approved" && deal.vendor_paused) {
    it = { key: "paused", cls: "bg-slate-100 text-slate-500 border-slate-200", Icon: PauseCircle, label: "Paused" };
  } else if (norm === "approved") {
    it = { key: "approved", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2, label: "Live" };
  } else if (norm === "pending") {
    it = { key: "pending", cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: Clock, label: "Pending review" };
  } else if (norm === "pending_owner") {
    it = { key: "pending_owner", cls: "bg-purple-50 text-purple-700 border-purple-200", Icon: Clock, label: "Awaiting your approval" };
  } else if (norm === "owner_declined") {
    it = { key: "owner_declined", cls: "bg-red-50 text-red-700 border-red-200", Icon: XCircle, label: "You declined" };
  } else if (norm === "rejected") {
    it = { key: "rejected", cls: "bg-red-50 text-red-700 border-red-200", Icon: XCircle, label: "Rejected" };
  } else {
    it = { key: norm || "unknown", cls: "bg-slate-50 text-slate-700 border-slate-200", Icon: Clock, label: deal.status || "Unknown" };
  }
  const Icon = it.Icon;
  return (
    <span
      data-testid={`my-deal-status-${it.key}`}
      className={`inline-flex items-center gap-1 rounded-full border ${it.cls} px-2.5 py-1 text-[11px] font-bold`}
    >
      <Icon className="w-3.5 h-3.5" /> {it.label}
    </span>
  );
}

function MyDealRow({ deal: d }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const status = (d.status || "").toLowerCase();
  const approved = status === "approved";
  const live = approved && !d.vendor_paused;
  const price = formatDealPrice(d);
  const [redeemOpen, setRedeemOpen] = useState(false);
  const [perfOpen, setPerfOpen] = useState(false);

  const refresh = () => qc.invalidateQueries({ queryKey: ["my-deals"] });

  const pauseMut = useMutation({
    mutationFn: async () => sharedApi.post(`/classifieds/deals/${d.id}/vendor-pause`),
    onSuccess: () => { refresh(); toast.success("Deal paused — it's hidden from the Deals page. Claimed vouchers stay redeemable."); },
    onError: (err) => toastErr(err, "Could not pause this deal"),
  });
  const resumeMut = useMutation({
    mutationFn: async () => sharedApi.post(`/classifieds/deals/${d.id}/vendor-resume`),
    onSuccess: () => { refresh(); toast.success("Deal resumed — it's live again."); },
    onError: (err) => toastErr(err, "Could not resume this deal"),
  });
  const deleteMut = useMutation({
    mutationFn: async () => (await sharedApi.delete(`/classifieds/deals/${d.id}/vendor`)).data,
    onSuccess: (data) => {
      refresh();
      const n = data?.cancelled_vouchers || 0;
      toast.success(n > 0 ? `Deal deleted — ${n} unused voucher${n === 1 ? "" : "s"} cancelled.` : "Deal deleted.");
    },
    onError: (err) => toastErr(err, "Could not delete this deal"),
  });

  return (
    <div data-testid={`my-deal-row-${d.id}`} className="rounded-2xl bg-white shadow-card overflow-hidden flex flex-col md:flex-row">
      {d.images?.[0] && (
        <div className="md:w-56 shrink-0 bg-slate-100">
          <img src={d.images[0]} alt={d.title} className="w-full h-40 md:h-full object-cover" loading="lazy" />
        </div>
      )}
      <div className="p-5 flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-display font-bold text-lg text-slate-900 truncate">{d.title}</h3>
          <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
            {d.is_owner && (
              <span
                data-testid={`my-deal-vendor-chip-${d.id}`}
                className="inline-flex items-center gap-1 rounded-full border border-orange-200 bg-orange-50 text-orange-700 px-2.5 py-1 text-[11px] font-bold"
              >
                <Store className="w-3.5 h-3.5" /> Vendor
              </span>
            )}
            <StatusChip deal={d} />
          </div>
        </div>
        <div className="mt-1 text-xs text-slate-500">Submitted {timeAgo(d.created_at)}</div>
        {price?.payLine && <div className="mt-2 text-sm font-semibold text-slate-800">{price.payLine}</div>}
        {approved && (
          <div className="mt-2 inline-flex items-center gap-1.5 text-sm text-slate-600" data-testid={`my-deal-voucher-stats-${d.id}`}>
            <Ticket className="w-4 h-4 text-[color:var(--brand-orange)]" />
            <span className="font-semibold">{d.vouchers_claimed ?? 0}</span> claimed ·{" "}
            <span className="font-semibold">{d.vouchers_redeemed ?? 0}</span> redeemed
          </div>
        )}
        {status === "rejected" && d.reject_reason && (
          <div
            data-testid={`my-deal-reject-reason-${d.id}`}
            className="mt-3 rounded-lg bg-red-50 border border-red-100 p-3 text-sm text-red-800"
          >
            <span className="font-bold">Rejection reason: </span>
            {d.reject_reason}
          </div>
        )}

        <div className="mt-4 flex items-center gap-2 flex-wrap">
          {live && (
            <Link
              to={`/deals/${d.id}`}
              data-testid={`my-deal-view-${d.id}`}
              className="rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white text-sm font-bold px-4 py-2"
            >
              View live deal
            </Link>
          )}
          {approved && !d.vendor_paused && (
            <button
              type="button"
              data-testid={`mdl-pause-${d.id}`}
              disabled={pauseMut.isPending}
              onClick={() => {
                if (window.confirm("Pause this deal? It leaves the public Deals page; already-claimed vouchers stay redeemable.")) pauseMut.mutate();
              }}
              className="mdl-chip"
            >
              <PauseCircle className="w-4 h-4" /> Pause
            </button>
          )}
          {approved && d.vendor_paused && (
            <button
              type="button"
              data-testid={`mdl-resume-${d.id}`}
              disabled={resumeMut.isPending}
              onClick={() => resumeMut.mutate()}
              className="mdl-chip text-emerald-700"
            >
              <PlayCircle className="w-4 h-4" /> Resume
            </button>
          )}
          {approved && (
            <button
              type="button"
              data-testid={`mdl-redeem-${d.id}`}
              onClick={() => setRedeemOpen((v) => !v)}
              className="mdl-chip text-[color:var(--brand-blue)]"
            >
              <Ticket className="w-4 h-4" /> Redeem a voucher
            </button>
          )}
          {approved && (
            <button
              type="button"
              data-testid={`mdl-perf-${d.id}`}
              onClick={() => setPerfOpen((v) => !v)}
              className="mdl-chip"
              aria-expanded={perfOpen}
            >
              <BarChart3 className="w-4 h-4" /> Performance
            </button>
          )}
          <button
            type="button"
            data-testid={`mdl-edit-${d.id}`}
            onClick={() => nav(`/deals/post?edit=${d.id}`)}
            className="mdl-chip"
          >
            <Pencil className="w-4 h-4" /> Edit
          </button>
          <button
            type="button"
            data-testid={`mdl-delete-${d.id}`}
            disabled={deleteMut.isPending}
            onClick={() => {
              if (window.confirm("Delete this deal? Unused vouchers will be cancelled. This can't be undone.")) deleteMut.mutate();
            }}
            className="mdl-chip text-red-600 hover:border-red-300"
          >
            <Trash2 className="w-4 h-4" /> Delete
          </button>
        </div>

        {redeemOpen && <RedeemBox deal={d} onDone={refresh} />}
        {perfOpen && <PerfChart dealId={d.id} />}
      </div>
      <style>{`.mdl-chip{display:inline-flex;align-items:center;gap:.35rem;font-size:.8rem;font-weight:600;padding:.45rem .9rem;border-radius:9999px;border:1px solid var(--border-soft);color:#334155;background:#fff;transition:border-color .2s;}
      .mdl-chip:hover{border-color:var(--brand-blue);}
      .mdl-chip:disabled{opacity:.5;pointer-events:none;}`}</style>
    </div>
  );
}

function toastErr(err, fallback) {
  const detail = err?.response?.data?.detail;
  toast.error(typeof detail === "string" ? detail : fallback);
}

/**
 * Iter 751 — typed-code redemption. Lookup `AS-XXXX-XXXX` →
 * GET /deal-vouchers/lookup?code= → verify it belongs to THIS deal →
 * POST /deal-vouchers/{id}/redeem.
 */
function RedeemBox({ deal, onDone }) {
  const [code, setCode] = useState("");
  const [found, setFound] = useState(null);

  const lookupMut = useMutation({
    mutationFn: async () =>
      (await sharedApi.get("/classifieds/deal-vouchers/lookup", { params: { code: code.trim().toUpperCase() } })).data,
    onSuccess: (v) => {
      if (v?.deal_id && v.deal_id !== deal.id) {
        setFound(null);
        toast.error("That voucher belongs to a different deal.");
        return;
      }
      setFound(v);
    },
    onError: (err) => { setFound(null); toastErr(err, "Voucher not found — check the code."); },
  });

  const redeemMut = useMutation({
    mutationFn: async () => (await sharedApi.post(`/classifieds/deal-vouchers/${found.id}/redeem`)).data,
    onSuccess: () => {
      toast.success("Voucher redeemed");
      setFound(null);
      setCode("");
      onDone?.();
    },
    onError: (err) => toastErr(err, "Could not redeem this voucher"),
  });

  const state = (found?.status || found?._effective_status || "").toLowerCase();
  const redeemable = found && (state === "active" || state === "");

  return (
    <div className="mt-4 rounded-xl border border-[color:var(--brand-blue)]/25 bg-[color:var(--brand-blue-wash)]/50 p-4" data-testid={`mdl-redeem-box-${deal.id}`}>
      <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Redeem a voucher</div>
      <form
        className="mt-2 flex items-center gap-2 flex-col sm:flex-row"
        onSubmit={(e) => { e.preventDefault(); if (code.trim() && !lookupMut.isPending) lookupMut.mutate(); }}
      >
        <input
          data-testid={`mdl-redeem-code-${deal.id}`}
          value={code}
          onChange={(e) => { setCode(e.target.value.toUpperCase().slice(0, 14)); setFound(null); }}
          placeholder="AS-XXXX-XXXX"
          className="w-full sm:flex-1 rounded-xl border border-soft bg-white px-3 py-2.5 font-mono text-sm tracking-widest outline-none focus:border-[color:var(--brand-blue)]"
        />
        <button
          type="submit"
          data-testid={`mdl-redeem-lookup-${deal.id}`}
          disabled={!code.trim() || lookupMut.isPending}
          className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] disabled:opacity-50 text-white text-sm font-bold px-4 py-2.5 whitespace-nowrap"
        >
          {lookupMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          Look up
        </button>
      </form>
      {found && (
        <div className="mt-3 rounded-lg bg-white border border-soft p-3 text-sm" data-testid={`mdl-redeem-found-${deal.id}`}>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="min-w-0">
              <div className="font-mono font-bold text-slate-900">{found.code}</div>
              <div className="text-xs text-slate-500 mt-0.5">
                {found.customer_name || found.customer || found.customer_email || "Customer"} ·{" "}
                <span className={`font-semibold ${redeemable ? "text-emerald-600" : "text-slate-400"}`}>{state || "active"}</span>
              </div>
            </div>
            <button
              type="button"
              data-testid={`mdl-redeem-confirm-${deal.id}`}
              disabled={!redeemable || redeemMut.isPending}
              onClick={() => redeemMut.mutate()}
              className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-bold px-4 py-2"
            >
              {redeemMut.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              Mark as redeemed
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Iter 757 — 14-day performance mini chart. Stacked bars per day:
 * grey views / orange claims / green redemptions, plus a totals row.
 */
function PerfChart({ dealId }) {
  const q = useQuery({
    queryKey: ["deal-perf", dealId],
    queryFn: async () =>
      (await sharedApi.get(`/classifieds/deals/${dealId}/vendor-stats`, { params: { days: 14 } })).data,
    staleTime: 60_000,
    retry: false,
  });

  if (q.isLoading) {
    return (
      <div className="mt-4 py-6 text-center text-slate-400" data-testid={`mdl-perf-chart-${dealId}`}>
        <Loader2 className="w-5 h-5 mx-auto animate-spin" />
      </div>
    );
  }
  if (q.isError) {
    return (
      <div className="mt-4 rounded-xl border border-soft bg-slate-50 p-4 text-sm text-slate-500" data-testid={`mdl-perf-chart-${dealId}`}>
        Performance stats aren&apos;t available for this deal.
      </div>
    );
  }
  const series = q.data?.series || [];
  const totals = q.data?.totals || { views: 0, claims: 0, redemptions: 0 };
  const maxDay = Math.max(1, ...series.map((s) => (s.views || 0) + (s.claims || 0) + (s.redemptions || 0)));

  return (
    <div className="mt-4 rounded-xl border border-soft bg-slate-50/70 p-4" data-testid={`mdl-perf-chart-${dealId}`}>
      <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Last 14 days</div>
      <div className="mt-3 flex items-end gap-1 h-24">
        {series.map((s) => {
          const total = (s.views || 0) + (s.claims || 0) + (s.redemptions || 0);
          const hPct = (n) => `${Math.round(((n || 0) / maxDay) * 100)}%`;
          return (
            <div key={s.day} className="flex-1 flex flex-col justify-end h-full" title={`${s.day}: ${s.views || 0} views · ${s.claims || 0} claims · ${s.redemptions || 0} redeemed`}>
              {total === 0 ? (
                <div className="w-full rounded-sm bg-slate-200" style={{ height: "2px" }} />
              ) : (
                <>
                  <div className="w-full rounded-t-sm bg-emerald-500" style={{ height: hPct(s.redemptions) }} />
                  <div className="w-full bg-[color:var(--brand-orange)]" style={{ height: hPct(s.claims) }} />
                  <div className="w-full rounded-b-sm bg-slate-300" style={{ height: hPct(s.views) }} />
                </>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex items-center justify-between gap-2 flex-wrap text-xs text-slate-600">
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-slate-300 inline-block" /> Views</span>
          <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-[color:var(--brand-orange)] inline-block" /> Claims</span>
          <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500 inline-block" /> Redeemed</span>
        </div>
        <div className="font-semibold" data-testid={`mdl-perf-totals-${dealId}`}>
          {totals.views || 0} views · {totals.claims || 0} claims · {totals.redemptions || 0} redeemed
        </div>
      </div>
    </div>
  );
}
